import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import useToggleButtonGroup from '../ToggleButtonGroup/useToggleButtonGroup';
import { ACTIVETOGGLE, INACTIVETOGGLE } from './styles';

export type ToggleButtonProps = {
  /**
   * Label of the button
   */
  text?: string;
  on?: boolean;
  onToggle?(on: boolean): void;
  disableToggle?: boolean;
  value: string | number;
  dataTest?: string;
  preserveLinebreaks?: boolean;
};

export const ToggleButton: FC<ToggleButtonProps> = ({
  text,
  on = false,
  onToggle,
  disableToggle,
  value,
  dataTest,
  preserveLinebreaks,
}: ToggleButtonProps) => {
  const { onGroupToggle, isGroupOn } = useToggleButtonGroup();
  const [isOn, setIsOn] = useState(on);

  useEffect(() => {
    setIsOn(on);
  }, [on]);

  useEffect(() => {
    if (isGroupOn) {
      setIsOn(isGroupOn(value));
    }
  }, [isGroupOn, value]);

  const onClick = useCallback(() => {
    const on = !isOn;
    setIsOn(on);

    if (onToggle) {
      onToggle(on);
    }
    if (onGroupToggle) {
      onGroupToggle(on, value);
    }
  }, [value, isOn, onToggle, onGroupToggle]);

  const handleToggleClasses = useMemo(
    () =>
      `${disableToggle ? 'pointer-events-none' : ''} ${
        isOn ? ACTIVETOGGLE : INACTIVETOGGLE
      } ${preserveLinebreaks && ' whitespace-pre'}`,
    [disableToggle, isOn, preserveLinebreaks],
  );

  return (
    <button
      className={handleToggleClasses}
      onClick={onClick}
      data-testid={dataTest}
    >
      {text ?? value}
    </button>
  );
};
